import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatISO, parseISO } from "date-fns";
import currentLocale from "~/src/helpers/locales";
import { RefObject, SyntheticEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  HjorteviltBrukereV1JaktomraderSharedJaktomradeResponse,
  HjorteviltJaktdataV1JaktdagCreateJaktdagMedIndividCreateJaktdagMedIndividRequest,
  HjorteviltJaktdataV1JaktdagSharedJaktdagResponse,
} from "~/src/api/generated/api";
import { Individ, Jaktdag, JaktdagResponse, NO_CONNECTION_STATUS_CODE } from "~/src/api/types";
import { jaktdataApi } from "~/src/appGlobals";
import { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import { ButtonLayout } from "~/src/components/Button/ButtonDefault";
import { dateFormatISO } from "~/src/components/CalendarPicker/CalendarConstants";
import { DialogAlignment } from "~/src/components/Dialog/DialogDefault";
import { ComboboxOption } from "~/src/components/Form/Combobox/Combobox";
import BoldText from "~/src/components/Text/BoldText";
import { TextVariant } from "~/src/components/Text/Text";
import AppDataContext from "~/src/contexts/AppDataContext/DataProvider";
import { StorageContext, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";
import useJaktdataHelper from "~/src/contexts/StorageContext/useJaktdataHelper";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import { toErrorRecords } from "~/src/helpers/formErrors";
import { isApiError } from "~/src/helpers/isApiErrorHelper";
import { formatISOWithoutTimestamp, hentJaktaar } from "~/src/helpers/time";
import { useClientState } from "~/src/hooks/useClientState";
import { useConfirmationDialog } from "~/src/hooks/useConfirmationDialog";
import { useDialog } from "~/src/hooks/useDialog";
import useFormData from "~/src/hooks/useFormData";
import { useStateful } from "~/src/hooks/useStateful";
import { getJaktdagerQuery } from "~/src/react-query/queries";
import { identifierForJaktdag, parseErrors } from "../../helpers";
import { elgFormValidation } from "../RegistrerElg/ElgFormValidation";
import { parseInitialDataElg, toJaktdagRequestElg } from "../RegistrerElg/ElgHelpers";
import {
  JaktdagRegistrationDataElg,
  settElgFields,
  skuttElgFields,
} from "../RegistrerElg/ElgTypes";
import { hjortFormValidation } from "../RegistrerHjort/HjortFormValidation";
import { parseInitialDataHjort, toJaktdagRequestHjort } from "../RegistrerHjort/HjortHelpers";
import {
  JaktdagRegistrationDataHjort,
  TermiskMap,
  settHjortInnmarkDataKeys,
  settHjortUtmarkDataKeys,
  skuttHjortInnmarkDataKeys,
  skuttHjortUtmarkDataKeys,
} from "../RegistrerHjort/HjortTypes";
import { handleOfflineJaktdagCreateOrUpdate } from "./helpers";
import SuccessDialogButtons from "./SuccessDialogButtons";

export default function useRegistrerJaktdag(
  initialData: HjorteviltJaktdataV1JaktdagSharedJaktdagResponse | undefined,
  scrollTopRef: RefObject<HTMLDivElement>,
  art: "Hjort" | "Elg",
) {
  const isSubmitting = useStateful(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useContext(UIContext);
  const appData = useContext(AppDataContext);
  const initialFormData =
    art === "Hjort"
      ? parseInitialDataHjort(initialData, appData)
      : parseInitialDataElg(initialData, appData);
  const { t } = useTranslation();
  const fellingsRapportLevert = useStateful(false);
  const storage = useContext(StorageContext);
  const { jaktdagId } = useParams();
  const eksisterendeJaktdag = useStateful<JaktdagResponse | undefined>(undefined);
  const { updateJaktdagerFromStore } = useJaktdataHelper();
  const hasGodkjenteDyr = initialData && initialData.Individer?.some((i) => i.Godkjent);
  const clientState = useClientState();
  const isOnline = clientState === "Online";
  const [formData, setFormData, removeFormData] = useFormData<
    JaktdagRegistrationDataHjort | JaktdagRegistrationDataElg
  >(initialFormData);

  const [formErrorsElg, setFormErrorsElg] = useState<
    Partial<Record<keyof JaktdagRegistrationDataElg, string>>
  >({});

  const [formErrorsHjort, setFormErrorsHjort] = useState<
    Partial<Record<keyof JaktdagRegistrationDataHjort, string>>
  >({});

  const resetForm = () => {
    initialFormData && setFormData(initialFormData);
  };

  const date = useStateful<Date>((formData.Dato && parseISO(formData.Dato)) || new Date());

  const initialSelected = (formData && {
    label: formData?.jaktomradeNavn || "",
    id: formData?.Jaktfelt || "",
    data: {},
  }) || { label: "", id: "", data: {} };

  const selectedJaktomrade = useStateful<ComboboxOption>(initialSelected);

  const schema =
    art === "Hjort"
      ? hjortFormValidation(t, fellingsRapportLevert)
      : elgFormValidation(t, fellingsRapportLevert);

  const queryClient = useQueryClient();

  const jaktdagEksistererDialog = useConfirmationDialog({
    title: t("pages.registrering.dialogs.jaktdagEksisterer.title"),
    confirmButtonText: t("pages.registrering.dialogs.jaktdagEksisterer.confirmButtonText"),
    cancelButtonText: t("pages.registrering.dialogs.jaktdagEksisterer.cancelButtonText"),
    buttonLayout: ButtonLayout.Vertical,
  });

  const successDialog = useDialog({
    title: t("pages.registrering.dialogs.overforingSuccess.title"),
    message: (
      <BoldText
        text={t("pages.registrering.dialogs.overforingSuccess.message")}
        specificWords={[8]}
        variant={TextVariant.xlarge}
      />
    ),
    alignment: DialogAlignment.Start,
    buttonLayout: ButtonLayout.Vertical,
    isBottomModal: true,
    closeModal: false,
  });

  const eksisterendeJaktdager = useQuery({
    queryKey: [
      "jaktdager",
      {
        jaktomradeId: selectedJaktomrade.value?.id,
        sesong: hentJaktaar(),
      },
    ],
    queryFn: async (context) => {
      try {
        const jaktdager = await getJaktdagerQuery(context);
        const queryItems = jaktdager.map((jaktdag): StorageItem<JaktdagResponse> => {
          return {
            objectKey: { id: jaktdag.Id },
            isPending: false,
            data: jaktdag,
          };
        });
        return updateJaktdagerFromStore(queryItems, formData.Jaktfelt ?? undefined);
      } catch (_error) {
        return updateJaktdagerFromStore(
          queryClient.getQueryData([
            "jaktdager",
            {
              jaktomradeId: selectedJaktomrade.value?.id,
              sesong: hentJaktaar(),
            },
          ]) ?? [],
          formData.Jaktfelt ?? undefined,
        );
      }
    },
    enabled: !!formData.Jaktfelt && !!date.value && !!appData.jegeropplysninger.data,
  });

  useEffect(() => {
    setFormData({
      ...formData,
      Dato: format(date.value, dateFormatISO),
      Jaktfelt: selectedJaktomrade.value?.id || formData?.Jaktfelt || "",
      jaktomradeNavn: selectedJaktomrade.value?.label || formData?.jaktomradeNavn || "",
    });
    eksisterendeJaktdager.refetch();
    location.state = { routeProps: { valgtOmradekode: selectedJaktomrade.value.id } };
  }, [selectedJaktomrade.value, date.value]);

  //Check if jaktdag (entry w. same art, jeger, dato and jaktområde) exists
  useEffect(() => {
    if (eksisterendeJaktdager.isSuccess && eksisterendeJaktdager.data !== undefined) {
      const currentJaktdag = identifierForJaktdag(formData);
      eksisterendeJaktdag.set(undefined);

      eksisterendeJaktdager.data.forEach((jaktdag) => {
        if (!jaktdag.data) return;
        if (
          identifierForJaktdag(jaktdag.data) === currentJaktdag &&
          ((formData.Id === undefined && jaktdagId === undefined) ||
            (formData.Id !== jaktdag.data.Id && jaktdagId !== jaktdag.data.Id))
        ) {
          eksisterendeJaktdag.set(jaktdag.data);
          return;
        }
      });
    }
  }, [eksisterendeJaktdager.data, eksisterendeJaktdager.status, formData.Jaktfelt, formData.Dato]);

  useEffect(() => {
    eksisterendeJaktdag.value &&
      jaktdagEksistererDialog.show({
        message: t("pages.registrering.dialogs.jaktdagEksisterer.message", {
          dato: format(date.value, "d. MMM", { locale: currentLocale() }),
          art:
            art === "Hjort"
              ? t("fauna.art.hjort.artsnavn").toLowerCase()
              : t("fauna.art.elg.artsnavn").toLowerCase(),
          jaktomrade: formData.jaktomradeNavn,
        }),
        onCancel: () => {
          jaktdagEksistererDialog.close();
        },
        onConfirm: () => {
          navigate(`/registrering/jaktdag/${eksisterendeJaktdag.value?.Id}`);
          removeFormData();
        },
      });
  }, [eksisterendeJaktdag.value]);

  // Do counter validation on counter change
  useEffect(() => {
    const handler = setTimeout(() => {
      // Cleanup function to clear the timeout if the effect is called again before the delay
      const result = schema.safeParse(formData);
      if (result.success) {
        if (art === "Hjort") {
          setFormErrorsHjort({});
        } else {
          setFormErrorsElg({});
        }
        return;
      }
      if (art === "Hjort") {
        const fieldErrors: Partial<Record<keyof JaktdagRegistrationDataHjort, string>> = {};
        [
          ...settHjortInnmarkDataKeys,
          ...skuttHjortInnmarkDataKeys,
          ...settHjortUtmarkDataKeys,
          ...skuttHjortUtmarkDataKeys,
        ].forEach((field) => {
          fieldErrors[field] = result.error.issues.find(
            (error) => error.path[0] === field,
          )?.message;
        });
        setFormErrorsHjort({ ...formErrorsHjort, ...fieldErrors });
      } else {
        const fieldErrors: Partial<Record<keyof JaktdagRegistrationDataElg, string>> = {};
        [...settElgFields, ...skuttElgFields].forEach((field) => {
          fieldErrors[field] = result.error.issues.find(
            (error) => error.path[0] === field,
          )?.message;
        });
        setFormErrorsElg({ ...formErrorsElg, ...fieldErrors });
      }
    }, 50); // Adjust the debounce delay as needed

    return () => {
      clearTimeout(handler);
    };
  }, [formData]);

  const handleChangeJaktomrade = async (
    selected: ComboboxOption<HjorteviltBrukereV1JaktomraderSharedJaktomradeResponse> | null,
  ) => {
    selectedJaktomrade.set(selected || selectedJaktomrade.value);
    setFormData({
      ...formData,
      Jaktfelt: selected?.id || "",
      jaktomradeNavn: selected?.label || "",
    });
    if (art === "Hjort") {
      setFormErrorsHjort({ ...formErrorsHjort, Jaktfelt: "" });
      fellingsRapportLevert.set(selected?.data?.ÅpenForRegistreringHjort === false);
    } else {
      setFormErrorsElg({ ...formErrorsElg, Jaktfelt: "" });
      fellingsRapportLevert.set(selected?.data?.ÅpenForRegistreringElg === false);
    }
  };

  function handleOffline(
    request: HjorteviltJaktdataV1JaktdagCreateJaktdagMedIndividCreateJaktdagMedIndividRequest,
    mode: "create" | "update",
    registrertAv: string | null | undefined,
  ) {
    const jaktdagToStore = request as Jaktdag;
    jaktdagToStore.RegistrertAv = registrertAv;

    handleOfflineJaktdagCreateOrUpdate({
      mode,
      jaktdagToStore,
      initialFormData: initialData,
      appData,
      storage,
    });

    if (!isOnline) {
      showSnackbar({
        message: t("sync.noNetworkWillSyncLater"),
        severity: AlertVariant.Info,
      });
    }

    successDialog.show({
      buttons: <SuccessDialogButtons valgtOmradekode={selectedJaktomrade.value.id} />,
    });

    removeFormData();
    return;
  }

  const handleSubmit = async (event: SyntheticEvent, termiskMap?: TermiskMap) => {
    event.preventDefault();

    const _formData = {
      ...formData,
      RegistrertAv: initialData?.RegistrertAv,
      Id: identifierForJaktdag(formData),
    };

    const result = schema.safeParse(_formData);

    if (!result.success) {
      if (art === "Hjort") {
        const fieldErrors: Partial<Record<keyof JaktdagRegistrationDataHjort, string>> = {};
        result.error.issues.forEach((error) => {
          fieldErrors[error.path[0] as keyof JaktdagRegistrationDataHjort] = error.message;
        });
        setFormErrorsHjort(fieldErrors);
      } else {
        const fieldErrors: Partial<Record<keyof JaktdagRegistrationDataElg, string>> = {};
        result.error.issues.forEach((error) => {
          fieldErrors[error.path[0] as keyof JaktdagRegistrationDataElg] = error.message;
        });
        setFormErrorsElg(fieldErrors);
      }
      scrollTopRef.current?.scrollIntoView({ behavior: "smooth" });
      if (!result.success) return;
    }
    if (art === "Hjort") {
      setFormErrorsHjort({});
    } else {
      setFormErrorsElg({});
    }

    const inStore = storage.get<Jaktdag>({ id: initialData?.Id }, "Jaktdag");

    const mode =
      initialData && !(!!inStore?.errors && inStore.operation === "create") ? "update" : "create";

    const requestMethodFor =
      mode === "update"
        ? jaktdataApi.putJaktdag.bind(jaktdataApi)
        : jaktdataApi.postJaktdag.bind(jaktdataApi);

    isSubmitting.set(true);

    const skotneDyr = initialData?.Individer?.map((individ) => {
      const individData = storage.get<Individ>({ id: individ.Id }, "Individ")?.data ?? individ;
      return {
        ...individData,
        Dato:
          art === "Hjort"
            ? formatISO(date.value, { representation: "date" })
            : formatISOWithoutTimestamp(date.value),
        RegistrertDato: formatISO(date.value, { representation: "date" }),
        Jegernummer: formData.Jegernummer,
        Jaktfelt: formData.Jaktfelt,
      };
    });
    const changesJaktfeltOrDate =
      mode === "update" &&
      (new Date(initialFormData?.Dato ?? "").toDateString() !=
        new Date(formData.Dato ?? "").toDateString() ||
        initialFormData?.Jaktfelt != formData.Jaktfelt);

    try {
      const request =
        art === "Hjort"
          ? toJaktdagRequestHjort(
              formData as JaktdagRegistrationDataHjort,
              date.value,
              appData.jegeropplysninger.data?.Jegernummer,
              skotneDyr ?? undefined,
              changesJaktfeltOrDate,
              termiskMap,
            )
          : toJaktdagRequestElg(
              formData as JaktdagRegistrationDataElg,
              date.value,
              appData.jegeropplysninger.data?.Jegernummer,
              skotneDyr ?? undefined,
              changesJaktfeltOrDate,
            );

      const storedJaktdag = storage.get<JaktdagResponse>({ id: request.Id }, "Jaktdag");

      let _isOnline = (await jaktdataApi.getClientState()) === "Online";
      if (!_isOnline || storedJaktdag) {
        handleOffline(request, mode, _formData.RegistrertAv);
        return;
      }

      try {
        await requestMethodFor(request);

        const jaktdagFromApi = await jaktdataApi.getJaktdag(request.Id);
        const individIdeer: string[] = jaktdagFromApi?.Individer?.map((i) => i.Id ?? "") ?? [];

        await Promise.all(
          individIdeer.map((id) =>
            jaktdataApi
              .getIndivid(id)
              .then((i) => {
                queryClient.setQueryData(["individId-" + i.Id, { individId: i.Id }], i);
              })
              .catch((error) => {
                if (error.statusCode === NO_CONNECTION_STATUS_CODE) {
                  handleOffline(request, mode, _formData.RegistrertAv);
                  isSubmitting.set(false);
                  resetForm();
                  throw error;
                }
                isSubmitting.set(false);
                throw error;
              }),
          ),
        );

        await jaktdataApi.getJaktdager(hentJaktaar(), selectedJaktomrade.value.id);

        await jaktdataApi.getSkutteDyr(hentJaktaar(), selectedJaktomrade.value.id);

        removeFormData();
        successDialog.show({
          buttons: <SuccessDialogButtons valgtOmradekode={selectedJaktomrade.value.id} />,
        });
      } catch (error) {
        if (isApiError(error)) {
          if (error.statusCode === NO_CONNECTION_STATUS_CODE) {
            //timeout error, we cannot know if it was transferred or not, so we add an offline create.
            handleOffline(request, mode, _formData.RegistrertAv);
            isSubmitting.set(false);
            resetForm();
            return;
          }
          scrollTopRef.current?.scrollIntoView({ behavior: "smooth" });

          if (art === "Hjort") {
            setFormErrorsHjort(toErrorRecords<JaktdagRegistrationDataHjort>(error));
          } else {
            setFormErrorsElg(toErrorRecords<JaktdagRegistrationDataElg>(error));
          }
          const formattedErrors = parseErrors(error.message);
          showSnackbar({
            message: formattedErrors ?? t("pages.registrering.errors.noeGikkGalt"),
            severity: AlertVariant.Error,
          });
          } else {
            showSnackbar({
              message: t("pages.registrering.errors.noeGikkGalt"),
              severity: AlertVariant.Error,
            });
          }
        }
    } catch (error) {
      if (error instanceof Error) {
        isSubmitting.set(false);
        resetForm();
        if (error.message === "CanNotDelete") {
          showSnackbar({
            message: t("pages.registrering.errors.canNotDelete"),
            severity: AlertVariant.Warning,
          });
        } else if (error.message === "CanNotEditGodkjent") {
          showSnackbar({
            message: t("pages.registrerArt.kanIkkeEndreDatoOgJaktomrade"),
            severity: AlertVariant.Error,
          });
        } else {
          const formattedErrors = parseErrors(error.message);
          showSnackbar({
            severity: AlertVariant.Error,
            message: formattedErrors ?? t("pages.registrering.errors.noeGikkGalt"),
          });
        }
        } else {
          showSnackbar({
            severity: AlertVariant.Error,
            message: t("pages.registrering.errors.noeGikkGalt"),
          });
        }
    } finally {
      isSubmitting.set(false);
    }
  };
  return {
    handleChangeJaktomrade,
    handleSubmit,
    isSubmitting: isSubmitting.value,
    formData:
      art === "Hjort"
        ? (formData as JaktdagRegistrationDataHjort)
        : (formData as JaktdagRegistrationDataElg),
    formErrorsElg,
    formErrorsHjort,
    setFormData,
    setFormErrorsElg,
    setFormErrorsHjort,
    fellingsRapportLevert,
    date,
    selectedJaktomrade,
    eksisterendeJaktdag,
    loadingEksisterendeJaktdager:
      eksisterendeJaktdager.isLoading || eksisterendeJaktdager.isFetching,
    jaktdagEksistererDialog,
    successDialog,
    hasGodkjenteDyr,
  };
}
